/**
 * About Tab Component
 *
 * Displays general Pharma Medical Information content in the sidebar.
 */

import { Linking } from 'react-native';

import { DisclaimerText } from '@/components/MedicalOnDemand/DisclaimerText';
import { LI, UL } from '@/components/list';
import { Divider } from '@/components/ui/divider';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  IndicationParagraph,
  PharmaProductSupportTeam,
  Product,
} from '@/constants/medicalOnDemand';
import { useModTeamCopy } from '@/hooks/useModTeamCopy';

type ProductTeamDetailsProps = {
  pharmaProductSupportTeam: PharmaProductSupportTeam;
  selectedProduct?: Product | null;
  hideHeader?: boolean;
};

export function ProductTeamDetails({
  pharmaProductSupportTeam,
  selectedProduct,
  hideHeader,
}: ProductTeamDetailsProps) {
  const { name: teamName, about } = useModTeamCopy(pharmaProductSupportTeam);

  /**
   * Ensures "J&J Medical" and "Johnson & Johnson" stay on the same line using non-breaking spaces.
   */
  const processAboutText = (text: string) => {
    return text
      .replace(/Johnson & Johnson/g, 'Johnson\u00A0&\u00A0Johnson')
      .replace(/J&J Medical/g, 'J&J\u00A0Medical');
  };

  return (
    <VStack className="gap-3">
      <VStack className="gap-3">
        {!hideHeader && (
          <Text className="font-bold text-base text-primary-500">About {teamName}:</Text>
        )}
        {about.map((aboutSection) => (
          <Text
            key={aboutSection.slice(0, 32)}
            className="text-sm leading-[1.4] text-typography-900">
            {processAboutText(aboutSection)}
          </Text>
        ))}
      </VStack>

      {selectedProduct && (
        <VStack className="gap-3">
          <DisclaimerText>{selectedProduct.disclaimer}</DisclaimerText>
          <LinkText
            size="xs"
            className="text-center font-medium text-primary-500 underline"
            onPress={() => Linking.openURL(selectedProduct.prescribingInfoUrl)}>
            View full Prescribing Information
          </LinkText>
          <Divider className="bg-outline-200" />
          {selectedProduct.indicationAndUsage && (
            <VStack className="gap-3">
              <Text className="font-bold text-base text-primary-500">
                {`${selectedProduct.indicationLabel} and Usage`}
              </Text>
              {selectedProduct.indicationAndUsage.map((paragraph, index) =>
                typeof paragraph === 'string' ? (
                  <Text key={index} className="text-sm leading-[1.4] text-typography-900">
                    {paragraph}
                  </Text>
                ) : (
                  <VStack key={index} className="gap-1">
                    <Text className="text-sm leading-[1.4] text-typography-900">
                      {paragraph.intro}
                    </Text>
                    <UL>
                      {paragraph.bullets.map((bullet, bulletIndex) => (
                        <LI key={bulletIndex}>{bullet}</LI>
                      ))}
                    </UL>
                  </VStack>
                )
              )}
            </VStack>
          )}
        </VStack>
      )}
    </VStack>
  );
}
